<template>
  <v-main id="documentation">
    <documentation-toc content-selector="#documentation"></documentation-toc>
    <h1 class="display-2 font-weight-light text-uppercase pb-4">Vital Developers Portal Help</h1>
    <v-alert
        type="warning"
        dark
        dense
        icon="mdi-school"
        prominent
    >
      To make sure that you running last version of the portal, do not forget to do force reload the portal, from time
      to time.<br>
      Depending on your operating system all you need to do is to press the following key combination:<br>
      Windows: <kbd>Ctrl</kbd> + <kbd>F5</kbd><br>
      Mac/Apple: <kbd>command
      <v-icon dark>mdi-apple-keyboard-command</v-icon>
    </kbd> +
      <kbd>Shift
        <v-icon dark>mdi-apple-keyboard-shift</v-icon>
      </kbd> + <kbd>R</kbd><br>
      Linux: <kbd>Ctrl</kbd> + <kbd>F5</kbd>
    </v-alert>
    <h2 class="display-1 bold text-uppercase pb-4">Dashboard</h2>
    <p>After you log in, you normally see the portal’s
      <router-link to="/dashboard">dashboard</router-link>. Here you will see what is happening with
      your applications or any other important information.
    </p>
    <v-img
        src="/img/help-dashboard-screen.png"
        max-width="1200"
    ></v-img>
    <p>
      <v-alert
          color="blue-grey"
          dark
          dense
          icon="mdi-lightbulb"
          prominent
          class="d-inline-block"
      >
        Also, for every event you will receive an automatic email notification.
      </v-alert>
    </p>
    <v-img
        src="/img/help-dashboard-notifications.png"
        max-width="900"
    ></v-img>
    <h2 class="display-1 bold text-uppercase pb-4 mt-5">Applications</h2>
    <p>To create, check or update applications click “<router-link to="/applications">Applications</router-link>” in the left sidebar.
    </p>
    <v-img
        src="/img/help-applications.png"
        max-width="1200"
    ></v-img>
    <h3 class="display-1 bold text-uppercase pb-4">Creating an Application</h3>
    <p>To start the process of submitting new applications press “
      <router-link to="/applications/create">Submit New Application</router-link>
      ”.
    </p>
    <v-img
        src="/img/help-applications-submit-button.png"
        max-width="1200"
    ></v-img>
    <v-img
        src="/img/help-application-create-1.png"
        max-width="1200"
    ></v-img>
    <p>You have to select the type of the application (application option) and add applicants by pressing <kbd>Add
      Applicant</kbd>.</p>
    <v-img
        src="/img/help-application-create-2.png"
        max-width="1200"
    ></v-img>
    <p>After you have filled out the details of all applicants press <kbd>Submit Application</kbd> and wait until the
      system has
      completed uploading the data, <strong>do not refresh or close the page</strong> until you have been informed that
      application has
      been created and you have been redirected to the application page.</p>
    <h4 class="title pb-3 text-uppercase">Video of the application submitting process</h4>
    <video controls>
      <source src="/img/help-application-creation.mp4" type="video/mp4">
      Sorry, your browser doesn't support embedded videos.
    </video>
    <h3 class="display-1 bold text-uppercase py-4">Viewing and Updating Application</h3>
    <p>After an application has been created you always can access it via the
      <router-link to="/applications">applications list</router-link>
      by clicking on its number.
    </p>
    <v-img
        src="/img/help-application-open.png"
        max-width="1200"
    ></v-img>
    <p class="pt-4">When you open an application, you will be able to upload documents, update applicants’ details,
      submit tracking
      number of shipped documents and send messages to your application manager.</p>
    <v-img
        src="/img/help-application-view.png"
        max-width="1200"
    ></v-img>
    <dl class="d-flex flex-row pb-3 align-top">
      <dt class="d-inline-flex font-weight-bold title pr-3 text-uppercase">Progress</dt>
      <dd class="d-inline-flex pt-1">here you will see at what stage your application is.</dd>
    </dl>
    <dl class="d-flex flex-row pb-3 align-top">
      <dt class="d-inline-flex font-weight-bold title pr-3 text-uppercase">Applicants</dt>
      <dd class="d-inline-flex pt-1">you can view and update details of applicants, also if you need to make more
        significant changes you have to ask the Vital Developers's manager responsible for handling your application using Messages.
      </dd>
    </dl>
    <dl class="d-flex flex-row pb-3 align-top">
      <dt class="d-inline-flex font-weight-bold title pr-3 text-uppercase">Messages</dt>
      <dd class="d-inline-flex pt-1">that’s where all conversations are going to take place, when you send a message the
        Vital Developers's manager responsible for your application will be notified and will respond to you as soon as possible.
      </dd>
    </dl>
    <dl class="d-flex flex-row pb-3 align-top">
      <dt class="d-inline-flex font-weight-bold title pr-3 text-uppercase">Documents</dt>
      <dd class="d-inline-flex pt-1">you are going to upload all requested documents here. You can upload documents
        directly by pressing upload
        <v-icon class="px-3">mdi-cloud-upload</v-icon>
        button and then selecting a folder.
      </dd>
    </dl>
    <v-img
        src="/img/help-document-upload-dialog.png"
        max-width="1200"
    ></v-img>
    <p class="display-2 font-weight-light text-center">OR</p>
    <p class="pl-10">You can just drag and drop file to the necessary folder.</p>
    <video controls>
      <source src="/img/help-document-drag-and-drop.mp4" type="video/mp4">
      Sorry, your browser doesn't support embedded videos.
    </video>
    <dl class="d-flex flex-row pb-3 align-top pt-6">
      <dt class="d-inline-flex font-weight-bold title pr-3 text-uppercase">Courier History</dt>
      <dd class="d-inline-flex pt-1">here you can get or add tracking number for the documents sent by courier service
        by pressing
        <v-icon class="px-3">mdi-email-plus</v-icon>
        button.
      </dd>
    </dl>
    <v-img
        src="/img/help-courier-add-tracking.png"
        max-width="1200"
    ></v-img>
    <h2 class="display-1 bold text-uppercase py-4">Sending Messages</h2>
    <p>Messaging system supports two type of messages:</p>
    <ul class="pb-4">
      <li class="pb-2"><v-icon>mdi-send</v-icon> <strong>Regular Messages</strong> – standard message with standard type of notifications;</li>
      <li class="pb-2"><v-icon>mdi-send-check</v-icon> <strong>Message with Response Request</strong> – this message will be displayed in other party dashboard widget "Unanswered Messages" and after 24 hours period
        it will raise an permanent alert showing count of messages which overdue 24 hours response window.
        Alert will be removed as soon as message will be answered.</li>
    </ul>
    <video controls>
      <source src="/img/help-messaging-send.mp4" type="video/mp4">
      Sorry, your browser doesn't support embedded videos.
    </video>
    <h2 class="display-1 bold text-uppercase py-4">Formatting Messages</h2>
    <p>Messages support simple formatting, separate paragraphs and lists by using an empty line which will result in
      nicely formatted message. Here are few examples:</p>
    <div class="row" style="max-width: 1200px;">
      <div class="col-sm-12 col-md-6">
        <v-card outlined>
          <v-card-text>
            <p class="display-1 text--primary">
              Bullet List
            </p>
            <p class="text-uppercase">Your input:</p>
            <div class="text--primary">
              Test sentence.<br>
              <br>
              * List item 1<br>
              * List item 2<br>
              * List item 3<br>
              * List item 4<br>
              <br>
              Another test sentence.
            </div>
            <p class="text-uppercase font-weight-bold pt-3">Result:</p>
            <div class="text--primary">
              <p>Test sentence.</p>
              <ul>
                <li>List item 1</li>
                <li>List item 2</li>
                <li>List item 3</li>
                <li>List item 4</li>
              </ul>
              <p>Another test sentence.</p>
            </div>
          </v-card-text>
        </v-card>
      </div>
      <div class="col-sm-12 col-md-6">
        <v-card outlined>
          <v-card-text>
            <p class="display-1 text--primary">
              Ordered List
            </p>
            <p class="text-uppercase">Your input:</p>
            <div class="text--primary">
              Test sentence.<br>
              <br>
              1. List item 1<br>
              2. List item 2<br>
              3. List item 3<br>
              4. List item 4<br>
              <br>
              Another test sentence.
            </div>
            <p class="text-uppercase font-weight-bold pt-3">Result:</p>
            <div class="text--primary">
              <p>Test sentence.</p>
              <ol>
                <li>List item 1</li>
                <li>List item 2</li>
                <li>List item 3</li>
                <li>List item 4</li>
              </ol>
              <p>Another test sentence.</p>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
    <p>Here’s a short video showing how it works:</p>
    <video controls>
      <source src="/img/help-messaging-format.mp4" type="video/mp4">
      Sorry, your browser doesn't support embedded videos.
    </video>
    <h2 class="display-1 bold text-uppercase py-4">Using Emojis in Messages</h2>
    <p>Emojis aren’t just for your phone anymore! The emoji keyboard was added in Windows 10 and 11, and was in MacOS for the longest while. To use it:</p>
    <p><strong>On Windows:</strong></p>
    <ul>
      <li>During text entry, type Windows logo key <v-icon class="px-3">mdi-microsoft-windows</v-icon> + <kbd>.</kbd> (period). The emoji keyboard will appear;</li>
      <li>Select an emoji with the mouse, or keep typing to search through the available emojis for one you like.</li>
    </ul>
    <p><strong>On MacOS:</strong></p>
    <ul>
      <li>Position your cursor where you want to add an emoji and press <kbd>Control</kbd> + <kbd>Command</kbd> + <kbd>Space</kbd>;</li>
      <li>Choose your emoji from the list;</li>
      <li>Click to add the emoji to your text.</li>
    </ul>
    <h2 class="display-1 bold text-uppercase py-4">Application Alerts</h2>
    <p>If you noticed, there is an Alert block on the
      <router-link to="/dashboard">Dashboard</router-link>. Application Alert is initiated when your application
      manager needs to inform you or request some action, like, for example: <strong>upload missing documents</strong>.
      You will receive
      an email notification and alerts to act and this can be found on the
      <router-link to="/dashboard">Dashboard</router-link>.
    </p>
    <v-img
        src="/img/help-dashboard-screen.png"
        max-width="1200"
    ></v-img>
    <p>Press follow button (
      <v-icon class="px-3">mdi-page-next</v-icon>
      ) on the right side of the alert to open corresponding application.
    </p>
    <p>On top of the application page you will see an alert, after you acted upon it, please press <kbd>Done</kbd> to
      notify your
      application manager.</p>
    <v-img
        src="/img/help-application-alert.png"
        max-width="1200"
    ></v-img>
    <h2 class="display-1 bold text-uppercase py-4">Technical Support</h2>
    <p>
      Please describe the issue that you are facing. Include any relevant information like where the issue is occuring
      or the error message.
    </p>
    <p>
      Our support team will contact you via provided email.
    </p>
    <v-row>
      <v-col cols="12" class="col-md-6 offset-md-3">
        <support-form></support-form>
      </v-col>
    </v-row>
  </v-main>
</template>
<script>
    import DocumentationToc from '../components/App/DocumentationToc'
    import SupportForm from '../components/App/SupportForm'

    export default {
        components: {
            DocumentationToc,
            SupportForm
        }
    }
</script>
<style rel="stylesheet/scss" lang="scss" scoped>
  video {
    width: 100% !important;
    max-width: 1200px;
    height: auto;
  }
</style>